import { type JwtPayload, jwtDecode } from 'jwt-decode'
import { UserAttributes } from '../../store/user/interfaces'
import { embeddedConfigManager } from '../embedded/EmbeddedConfigManager'
import { AuthI, ExpireTimes, TokensFromAPI } from './interfaces'
import TokenServiceProvider from '../../services/TokenServiceProvider'
import { getUserAttributes } from '../embedded/GraphQlAPI'
import ShowNotification from '../../ui-components/modals/ToastNotifcationNotiStack'
import { ToastNotificationVariant } from '../../ui-components/util/Interfaces'

const EXPIRE_VARIANCE = 60 * 1000 // If the token is within 1 minute of expiration, go ahead and retrieve a new one

export class EmbeddedAuth implements AuthI {
  private tokens?: TokensFromAPI

  private readonly expireTimes: ExpireTimes

  constructor() {
    this.expireTimes = {
      idToken: 0,
      accessToken: 0,
      refreshToken: 0,
    }
  }

  getCurrentToken = async (): Promise<string> => {
    if (!this.tokens || this.tokenIsExpired('id')) {
      const credentials = embeddedConfigManager.getPartsBasketCreds()
      try {
        this.tokens = await TokenServiceProvider.getTokens(credentials)
        const decoded = jwtDecode<JwtPayload>(this.tokens.idToken)
        this.expireTimes.idToken = (decoded.exp || 0) * 1000
      } catch (error) {
        if (error?.toString().includes('401')) {
          ShowNotification({
            message: `invalidCredentials`,
            variant: ToastNotificationVariant.ERROR,
          })
        } else {
          ShowNotification({
            message: `An error occurred while validating credentials.`,
            variant: ToastNotificationVariant.ERROR,
          })
        }
        throw error
      }
    }
    return this.tokens.idToken
  }

  getUserAttributes = async (): Promise<UserAttributes> => {
    const token = await this.getCurrentToken()
    if (!this.tokens) {
      throw new Error('User not initialized')
    }
    return getUserAttributes(this.tokens.userInfo.userId, token)
  }

  private tokenIsExpired = (tokenType: 'id' | 'refresh' | 'access') => {
    const expTime = this.expireTimes[`${tokenType}Token`]
    return expTime === 0 || Date.now() + EXPIRE_VARIANCE > expTime
  }

  public async signIn(_username: string, _password: string): Promise<unknown> {
    return this.getCurrentToken()
  }

  public signOut(): Promise<unknown> {
    return Promise.resolve()
  }

  public getUserId(): string {
    if (!this.tokens) {
      throw new Error('User not initialized')
    }
    return this.tokens.userInfo.userId.toString()
  }
}
